import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Field,
  IconButton,
  Input,
  PhoneInput,
  Select,
  Textarea,
  TimeInput,
} from '@/shared/ui';
import {
  DEFAULT_GROUP_ROWS,
  groupRows,
  type FormField,
  type GroupColumn,
  type GroupRow,
} from '@/shared/api/forms';
import { UserField } from './UserField';
import styles from './GroupField.module.css';

export const GroupField: React.FC<{
  field: FormField;
  value: unknown;
  disabled?: boolean;
  onChange: (rows: GroupRow[]) => void;
}> = ({ field, value, disabled, onChange }) => {
  const columns = field.columns ?? [];
  const stored = groupRows(value);
  const rows = stored.length > 0 ? stored : [{}];
  const limit = field.maxRows ?? DEFAULT_GROUP_ROWS;

  const setCell = (index: number, key: string, cell: unknown) => {
    const next = rows.map((row, i) => (i === index ? { ...row, [key]: cell } : row));
    onChange(next);
  };

  return (
    <div className={styles.group} role="group" aria-label={field.label}>
      <span className={styles.title}>
        {field.label}
        {field.required && <span aria-hidden> *</span>}
      </span>
      {field.hint && <p className={styles.hint}>{field.hint}</p>}

      {rows.map((row, index) => (
        <div key={index} className={styles.row}>
          <div className={styles.cells}>
            {columns.map((column) => (
              <GroupCell
                key={column.key}
                id={`ff-${field.key}-${index}-${column.key}`}
                column={column}
                value={row[column.key]}
                disabled={disabled}
                onChange={(cell) => setCell(index, column.key, cell)}
              />
            ))}
          </div>
          {rows.length > 1 && (
            <IconButton
              className={styles.remove}
              label={`Удалить строку ${index + 1}`}
              tooltip="Удалить строку"
              tone="danger"
              disabled={disabled}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <Trash2 size={13} aria-hidden />
            </IconButton>
          )}
        </div>
      ))}

      {rows.length < limit && (
        <Button
          className={styles.add}
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...rows, {}])}
        >
          <Plus size={14} aria-hidden />
          Добавить строку
        </Button>
      )}
    </div>
  );
};

const GroupCell: React.FC<{
  id: string;
  column: GroupColumn;
  value: unknown;
  disabled?: boolean;
  onChange: (value: unknown) => void;
}> = ({ id, column, value, disabled, onChange }) => {
  const text = (value as string) ?? '';
  const set = (next: string) => onChange(next === '' ? undefined : next);

  if (column.type === 'checkbox') {
    return (
      <div className={styles.checkCell}>
        <Checkbox
          checked={value === true}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked === true)}
          label={column.label}
        />
      </div>
    );
  }

  const control = (() => {
    switch (column.type) {
      case 'textarea':
        return (
          <Textarea
            id={id}
            className={styles.textarea}
            value={text}
            placeholder={column.placeholder}
            disabled={disabled}
            maxLength={5000}
            onChange={(e) => set(e.target.value)}
          />
        );
      case 'number':
        return (
          <Input
            id={id}
            type="number"
            value={value == null ? '' : String(value)}
            placeholder={column.placeholder}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          />
        );
      case 'date':
        return <DatePicker id={id} value={text} disabled={disabled} onChange={set} />;
      case 'time':
        return <TimeInput id={id} value={text} disabled={disabled} onChange={set} />;
      case 'user':
        return (
          <UserField
            id={id}
            value={value}
            allowOther
            placeholder={column.placeholder}
            disabled={disabled}
            onChange={onChange}
          />
        );
      case 'phone':
        return (
          <PhoneInput
            id={id}
            value={text}
            placeholder={column.placeholder}
            disabled={disabled}
            maxLength={20}
            onChange={set}
          />
        );
      case 'select':
        return (
          <Select
            id={id}
            aria-label={column.label}
            value={text}
            disabled={disabled}
            onValueChange={(v) => set(v ?? '')}
            options={[{ value: '', label: column.placeholder || 'Выберите…' }, ...(column.options ?? [])]}
          />
        );
      default:
        return (
          <Input
            id={id}
            type={column.type === 'email' ? 'email' : column.type === 'url' ? 'url' : undefined}
            value={text}
            placeholder={column.placeholder}
            disabled={disabled}
            maxLength={500}
            onChange={(e) => set(e.target.value)}
          />
        );
    }
  })();

  return (
    <Field label={column.label} required={column.required}>
      {control}
    </Field>
  );
};
